/**
 * Copy del directorio de jugadores (B11 web), en tuteo es-CR: nada de voseo (la
 * guardia vive en `players.copy.spec.ts` y solo mira este fichero; el repo es mixto a
 * propósito, N13). Verbatim de `docs/specs/players-web-plan.md` §5, con los cambios de
 * la auditoría de Alma del 30/09/2026.
 *
 * Lo que responde la API (403, 422, 429) se pinta tal cual desde el servidor; aquí
 * solo viven los fallbacks, que repiten el texto de la API o de B10.
 */
export const PLAYERS_COPY = {
  directory: {
    skeletonLabel: 'Cargando jugadores',
    guestTitle: 'Inicia sesión para ver a otros jugadores',
    guestBody:
      'El directorio es solo para personas con cuenta verificada en Perrón Store. Así cuidamos a quienes aparecen en él.',
    guestLogin: 'Inicia sesión',
    guestRegister: 'Crea tu cuenta',
    /** Solo tras un 401 con sesión (P1): la cabecera sigue con la cuenta, la sección no. */
    sessionExpired: 'Tu sesión caducó. Inicia sesión de nuevo para ver el directorio.',
    /** = `PlayersCopy::NOT_VERIFIED_DIRECTORY`; se pinta el del servidor si llega en el 403. */
    notVerifiedFallback: 'Verifica tu correo para ver el directorio de jugadores.',
    resend: 'Reenviar correo de verificación',
    resendSent: 'Te enviamos el correo de verificación. Revisa tu bandeja de entrada.',
    resendError: 'No pudimos reenviar el correo. Inténtalo de nuevo.',
    /** = `ACCOUNT_MAIL_RATE_LIMITED` de B10 §7.2; se pinta el del servidor si llega en el 429. */
    resendRateLimitedFallback:
      'Ya te lo enviamos hace un momento. Espera un poco antes de pedirlo otra vez.',
    gameFilterLabel: 'Filtrar por juego',
    allGames: 'Todos',
    provinceFilterLabel: 'Provincia',
    allProvinces: 'Todas',
    /** Quien mira nunca sale en la lista (B7): de ahí «otros». */
    emptyAll: 'Aún no hay otros jugadores en el directorio.',
    emptyGame: (game: string) => `No hay jugadores de ${game} todavía.`,
    emptyProvince: (province: string) => `No hay jugadores en ${province} todavía.`,
    emptyBoth: (game: string, province: string) =>
      `No hay jugadores de ${game} en ${province} todavía.`,
    /** Solo si el perfil propio no existe o no está visible. */
    emptyCta: 'Sé el primero — activa tu perfil',
    emptyMascotAlt: 'Mascota Perrón buscando jugadores',
    errorText: 'No pudimos cargar el directorio de jugadores.',
    errorMascotAlt: 'Mascota Perrón con un problema de conexión',
    retry: 'Reintentar',
    /** Enlace entero a `/contacto` (B26). */
    report: '¿Algún problema con un jugador? Escríbenos',
  },
  card: {
    listedSince: (day: number, month: string, year: string) =>
      `En el directorio desde el ${day} de ${month} de ${year}`,
    invite: 'Invitar a jugar',
    sent: 'Solicitud enviada',
    received: 'Te invitó: responde desde tu perfil',
    connected: 'Ya pueden jugar: ver contacto',
  },
  banner: {
    noProfileTitle: 'Aún no tienes perfil de jugador',
    noProfileBody:
      'Crea tu perfil para que otros jugadores te encuentren y puedan invitarte a jugar.',
    noProfileCta: 'Crear mi perfil',
    hiddenTitle: 'Tu perfil de jugador no está visible',
    hiddenBody: 'Revísalo y actívalo para que otros jugadores te encuentren.',
    hiddenCta: 'Activar mi perfil',
    /** = `PlayersCopy::SUSPENDED`, partido para enlazar «escríbenos» a `/contacto`. */
    suspendedBefore: 'Tu perfil de jugador está suspendido. Si crees que es un error, ',
    suspendedLink: 'escríbenos',
    suspendedAfter: '.',
  },
  /**
   * Texto de consentimiento (§5.6.3 del spec de la API, B9 + B31), versión
   * `PLAYERS_CONSENT_VERSION`. Partido para enlazar «Política de privacidad».
   * Lo usan J2 (interruptor) y J3 (primer envío).
   */
  consent: {
    before:
      'Al activar tu perfil, cualquier persona con cuenta verificada en Perrón Store podrá ver tu alias, tus juegos y tu zona. Tu forma de contacto solo la verá quien acepte jugar contigo, y puedes dejar de compartirla cuando quieras. Declaro que tengo 18 años o más, o permiso de mi madre, padre o persona encargada. Más detalles en la ',
    link: 'Política de privacidad',
    after: '.',
  },
  /** Pestaña «Comunidad» de `/perfil` (J2, D19-D24 del spec web). */
  profile: {
    skeletonLabel: 'Cargando tu perfil de jugador',
    loadError: 'No pudimos cargar tu perfil de jugador.',
    retry: 'Reintentar',
    titleNew: 'Crea tu perfil de jugador',
    titleEdit: 'Tu perfil de jugador',
    intro:
      'Elige un alias y tus juegos para que otros jugadores te encuentren. Tu nombre real nunca sale en el directorio.',
    statusVisible: 'Tu perfil está visible en el directorio.',
    hiddenReason: {
      inactive: 'Tu perfil no sale en el directorio porque «Busco jugadores» está desactivado.',
      no_consent: 'Tu perfil no sale en el directorio porque falta aceptar las condiciones.',
      no_games: 'Tu perfil no sale en el directorio porque no tiene ningún juego disponible.',
      unverified: 'Tu perfil no sale en el directorio porque tu correo no está verificado.',
    },
    aliasLabel: 'Alias',
    aliasHelp: 'Entre 3 y 20 caracteres: letras sin tildes ni ñ, números, guion o guion bajo.',
    provinceLabel: 'Provincia',
    provincePlaceholder: 'Elige tu provincia',
    cantonLabel: 'Cantón (opcional)',
    cantonNone: 'Sin cantón',
    gamesLabel: 'Juegos',
    gamesHelp: 'Elige los juegos que quieres jugar.',
    contactLabel: 'Cómo quieres que te contacten',
    contactHelp: 'Solo lo verá quien acepte jugar contigo.',
    contactMethods: {
      whatsapp: 'WhatsApp',
      discord: 'Discord',
      email: 'Correo',
      tienda: 'En la tienda',
    },
    contactValueLabel: {
      whatsapp: 'Tu número de WhatsApp',
      discord: 'Tu usuario de Discord',
    },
    contactEmail: (email: string) =>
      `Con quien aceptes compartiremos el correo de tu cuenta: ${email}`,
    bioLabel: 'Presentación (opcional)',
    bioHelp: 'Texto plano, sin enlaces.',
    bioCounter: (count: number, max: number) => `${count}/${max}`,
    emailNotificationsLabel: 'Avisarme por correo cuando reciba solicitudes',
    lookingLabel: 'Busco jugadores',
    lookingHelp: 'Actívalo para salir en el directorio.',
    /** = `PlayersCopy::NOT_VERIFIED`. */
    notVerifiedFallback: 'Verifica tu correo antes de activar tu perfil de jugador.',
    notVerifiedSavesInactive: 'Mientras no verifiques tu correo, tu perfil se guarda como inactivo.',
    consentAccepted: (version: string) =>
      `Aceptaste las condiciones del directorio (versión ${version})`,
    save: 'Guardar',
    saved: 'Guardamos tu perfil.',
    saveError: 'No pudimos guardar tu perfil. Inténtalo de nuevo.',
    delete: 'Borrar mi perfil de jugador',
    deleteConfirm:
      'Se borrará tu perfil, dejarás de salir en el directorio y se cancelarán tus solicitudes pendientes. Esto no se puede deshacer.',
    deleteYes: 'Sí, borrar',
    deleteNo: 'Cancelar',
    /** = `PlayersCopy::PROFILE_DELETED`; se pinta el del servidor si llega. */
    deletedFallback: 'Borramos tu perfil de jugador.',
    deleteError: 'No pudimos borrar tu perfil. Inténtalo de nuevo.',
    previewTitle: 'Así te verán otros jugadores',
    previewAliasPlaceholder: 'tu_alias',
  },
  /** Solicitudes para jugar (J3, D32-D35). Lo que manda la API se pinta del servidor. */
  requests: {
    modalTitle: (alias: string) => `Invitar a ${alias} a jugar`,
    gameLabel: 'Juego (opcional)',
    noGame: 'Sin juego concreto',
    messageLabel: 'Mensaje (opcional)',
    messageHelp: 'Texto plano, sin enlaces.',
    messageCounter: (count: number, max: number) => `${count}/${max}`,
    noticeBefore: 'Este jugador verá tu alias, tus juegos y tu zona. Tu forma de contacto solo se comparte si acepta. ',
    noticeLink: 'Más detalles',
    send: 'Enviar solicitud',
    cancel: 'Cancelar',
    closeLabel: 'Cerrar',
    /** = `PlayersCopy::REQUEST_SENT`. */
    sentFallback: '¡Listo! Tu solicitud va en camino. Verás la respuesta en tu perfil.',
    /** = `PlayersCopy::NEED_PROFILE`. */
    needProfileFallback: 'Crea tu perfil de jugador y acepta las condiciones antes de enviar solicitudes.',
    needProfileCta: 'Ir a mi perfil de jugador',
    /** = `PlayersCopy::NEED_VERIFIED` (solo para reconocerlo: se pinta el del servidor). */
    needVerifiedText: 'Verifica tu correo antes de enviar solicitudes.',
    /** = `PlayersCopy::PLAYER_NOT_FOUND`. */
    notFoundFallback: 'No encontramos a ese jugador.',
    /** = `PlayersCopy::RATE_LIMITED`. */
    rateLimitedFallback: 'Espera un momento antes de enviar otra solicitud.',
    sendError: 'No pudimos enviar tu solicitud. Inténtalo de nuevo.',

    // ── Bandejas, historial y contador (D33-D34) ──
    title: 'Solicitudes para jugar',
    boxLabel: 'Bandeja',
    inbox: 'Recibidas',
    inboxWithCount: (count: number) => `Recibidas (${count})`,
    sent: 'Enviadas',
    statusFilterLabel: 'Estado',
    allStatuses: 'Todas',
    statusLabels: {
      pendiente: 'Pendiente',
      aceptada: 'Aceptada',
      rechazada: 'Rechazada',
      cancelada: 'Cancelada',
      caducada: 'Caducada',
      cerrada: 'Cerrada',
    },
    /** `closed_by` en palabras, por estado (solo en los terminales; `null` no se pinta). */
    closedBy: {
      rechazada: { me: 'La rechazaste.', them: 'El otro jugador la rechazó.', system: 'Se cerró automáticamente.' },
      cancelada: { me: 'La cancelaste.', them: 'El otro jugador la canceló.', system: 'Se canceló automáticamente.' },
      caducada: { me: 'Caducó sin respuesta.', them: 'Caducó sin respuesta.', system: 'Caducó sin respuesta a los 7 días.' },
      cerrada: {
        me: 'Dejaste de compartir el contacto.',
        them: 'El otro jugador dejó de compartir el contacto.',
        system: 'Se cerró automáticamente.',
      },
    },
    /** = `PlayersCopy::COUNTERPART_UNAVAILABLE`. */
    unavailable: 'Este jugador ya no está disponible.',
    inboxGame: (game: string) => `Juego: ${game}`,
    createdAt: (date: string) => `Enviada el ${date}`,
    respondedAt: (date: string) => `Respondida el ${date}`,
    contactTitle: 'Contacto',
    contactMethodLabels: { whatsapp: 'WhatsApp', discord: 'Discord', email: 'Correo', tienda: 'En la tienda' },
    accept: 'Aceptar',
    reject: 'Rechazar',
    cancelRequest: 'Cancelar solicitud',
    close: 'Dejar de compartir contacto',
    closeConfirm:
      'Dejarás de ver el contacto de este jugador y este jugador dejará de ver el tuyo. Esto no se puede deshacer.',
    closeYes: 'Sí, dejar de compartir',
    closeNo: 'Cancelar',
    /** = `PlayersCopy::ACCEPTED`, `REJECTED`, `CANCELLED` y `CLOSED`; se pinta el del servidor si llega. */
    actionFallback: {
      accept: 'Aceptaste la solicitud. Ya puedes ver cómo contactar a este jugador.',
      reject: 'Rechazaste la solicitud.',
      cancel: 'Cancelaste la solicitud.',
      close: 'Dejaste de compartir tu contacto con este jugador.',
    },
    /** = `PlayersCopy::ALREADY_RESOLVED` (409). */
    alreadyResolvedFallback: 'Esta solicitud ya no está pendiente.',
    /** = `PlayersCopy::REQUEST_NOT_FOUND` (404). */
    notFoundRequestFallback: 'No encontramos esa solicitud.',
    actionError: 'No pudimos completar la acción. Inténtalo de nuevo.',
    emptyInbox: 'Aún no has recibido solicitudes.',
    emptySent: 'Aún no has enviado solicitudes.',
    emptyFiltered: 'No hay solicitudes con este estado.',
    emptyMascotAlt: 'Mascota Perrón esperando solicitudes',
    loadError: 'No pudimos cargar tus solicitudes.',
    retry: 'Reintentar',
    skeletonLabel: 'Cargando solicitudes',
  },
  /** Método de contacto «en la tienda» (§7.2 del spec de la API, B29). Lo usa J3. */
  store: {
    contactText:
      'Prefiere coordinar en Perrón Store. Pasa por la tienda y pregunta por las noches de juego.',
  },
} as const;
